import React from 'react';
import { Select } from 'antd';
import { Moment } from 'moment';
import { useIntl } from 'umi';

import { RecurrenceConfig } from '@/typings/models/Recurrence';
import { describeRecurrence, getRecurrencePresets, recurrenceConfigKey } from '@/utils/recurrence';
import CustomRecurrenceDrawer from './CustomRecurrenceDrawer';
import styles from './recurrence.less';

const CUSTOM_KEY = 'custom';

interface RecurrenceFieldProps {
  /** The recurrence rule, or null/undefined for "Does not repeat". Provided by ProForm.Item. */
  value?: RecurrenceConfig | null;
  onChange?: (config: RecurrenceConfig | null) => void;
  /** The event start date — anchors the preset labels (weekday/day-of-month). */
  start?: Moment | null;
  disabled?: boolean;
}

/**
 * Recurrence selector for the calendar-event form. Renders a Google-style dropdown
 * of presets ("Does not repeat", "Daily", "Weekly on …", …) plus a "Custom…" option
 * that opens {@link CustomRecurrenceDrawer}. A custom rule always shows as "Custom…"
 * in the select itself — its full description (which can run long, e.g. "Every 2
 * weeks on Monday, Wednesday, ending after 15 occurrences") is printed below instead.
 *
 * Designed to be dropped inside a `ProForm.Item name="recurrence"` — it follows the
 * `{ value, onChange }` contract that ProForm injects. Only meaningful when creating
 * a new custom event — a series' pattern can't be changed after creation, so callers
 * should pass `disabled` when editing an existing event.
 */
const RecurrenceField: React.FC<RecurrenceFieldProps> = ({ value, onChange, start, disabled }) => {
  const intl = useIntl();
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const presets = React.useMemo(() => getRecurrencePresets(start, intl), [start, intl]);

  const normalizedValue = recurrenceConfigKey(value);
  const matchedPreset = presets.find(
    (p) => !p.isCustom && recurrenceConfigKey(p.config) === normalizedValue,
  );
  // A non-empty value that matches no preset is an existing custom rule.
  const isCustomValue = Boolean(value) && !matchedPreset;

  const selectedKey = matchedPreset?.key ?? (isCustomValue ? CUSTOM_KEY : 'none');

  const options = React.useMemo(() => {
    const base = presets
      .filter((p) => !p.isCustom)
      .map((p) => ({ value: p.key, label: p.label }));
    base.push({
      value: CUSTOM_KEY,
      label: intl.formatMessage({ id: 'pages.calendar.recurrence.custom', defaultMessage: 'Custom recurrence…' }),
    });
    return base;
  }, [presets, intl]);

  const handleSelect = (key: string) => {
    if (key === CUSTOM_KEY) {
      setDrawerOpen(true);
      return;
    }
    const preset = presets.find((p) => p.key === key);
    onChange?.(preset?.config ?? null);
  };

  return (
    <>
      <Select
        size="large"
        style={{ width: '100%' }}
        value={selectedKey}
        options={options}
        disabled={disabled}
        // onSelect (not onChange) so re-opening the drawer to edit the current
        // custom rule works even though the "Custom…" option is already selected
        // (onChange only fires when the value actually changes).
        onSelect={handleSelect}
        data-testid="recurrence-select"
      />
      {isCustomValue && (
        <div className={styles.preview} data-testid="recurrence-custom-description">
          {describeRecurrence(value, start, intl)}
        </div>
      )}
      <CustomRecurrenceDrawer
        open={drawerOpen}
        value={value}
        start={start}
        onOk={(config) => {
          setDrawerOpen(false);
          onChange?.(config);
        }}
        onCancel={() => setDrawerOpen(false)}
      />
    </>
  );
};

export default RecurrenceField;
